import React, { useState, useEffect, useRef } from 'react';
import { AxiosInstanceWithCache } from '../../../components/utils';
import LinkPreview from '../linkPreview';

interface MediaCoverageItem {
  id: string;
  title: string;
  description: string;
  mediaLogo?: string;
  link: string;
  type: 'article' | 'video';
  createdAt: number;
}

interface MediaCoverageProps {
  className?: string;
  title: string;
  eventcode?: string;
}

const MediaCoverage: React.FC<MediaCoverageProps> = ({ className, title, eventcode }) => {
  const [mediaItems, setMediaItems] = useState<MediaCoverageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [articles, setArticles] = useState<MediaCoverageItem[]>([]);
  const [videos, setVideos] = useState<MediaCoverageItem[]>([]);
  const [visibleItems, setVisibleItems] = useState(3);
  
  // Articles carousel state
  const [articlesCurrentIndex, setArticlesCurrentIndex] = useState(0);
  const [articlesIsTransitioning, setArticlesIsTransitioning] = useState(false);
  const articlesCarouselRef = useRef<HTMLDivElement>(null);
  
  // Videos carousel state
  const [videosCurrentIndex, setVideosCurrentIndex] = useState(0);
  const [videosIsTransitioning, setVideosIsTransitioning] = useState(false);
  const videosCarouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchMediaCoverage();
  }, []);

  useEffect(() => {
    const updateVisibleItems = () => {
      setVisibleItems(window.innerWidth <= 768 ? 1 : 3);
    };

    updateVisibleItems();
    window.addEventListener('resize', updateVisibleItems);
    return () => window.removeEventListener('resize', updateVisibleItems);
  }, []);

  const fetchMediaCoverage = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await AxiosInstanceWithCache.get('/api/getmediacoverage');
      
      if (response.data.result === 'Success') {
        const data = response.data.data || [];
        setMediaItems(data);
        
        // Separate articles and videos
        const articlesData = data.filter(item => item.type === 'article');
        const videosData = data.filter(item => item.type === 'video');
        
        setArticles(articlesData);
        setVideos(videosData);
      } else {
        setError('Failed to fetch media coverage');
      }
    } catch (err) {
      console.error('Error fetching media coverage:', err);
      setError('Failed to load media coverage. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Articles carousel functions
  const nextArticlesSlide = () => {
    if (articles.length <= visibleItems) return;
    
    setArticlesIsTransitioning(true);
    setArticlesCurrentIndex((prevIndex) => {
      const nextIndex = prevIndex + 1;
      return nextIndex >= articles.length ? 0 : nextIndex;
    });
    
    setTimeout(() => setArticlesIsTransitioning(false), 500);
  };

  const prevArticlesSlide = () => {
    if (articles.length <= visibleItems || articlesIsTransitioning) return;
    
    setArticlesIsTransitioning(true);
    setArticlesCurrentIndex((prevIndex) => {
      const prev = prevIndex - 1;
      return prev < 0 ? articles.length - 1 : prev;
    });
    
    setTimeout(() => setArticlesIsTransitioning(false), 500);
  };

  const goToArticlesSlide = (index: number) => {
    if (articles.length <= visibleItems) return;
    
    setArticlesIsTransitioning(true);
    setArticlesCurrentIndex(index);
    
    setTimeout(() => setArticlesIsTransitioning(false), 500);
  };

  // Videos carousel functions
  const nextVideosSlide = () => {
    if (videos.length <= visibleItems) return;
    
    setVideosIsTransitioning(true);
    setVideosCurrentIndex((prevIndex) => {
      const nextIndex = prevIndex + 1;
      return nextIndex >= videos.length ? 0 : nextIndex;
    });
    
    setTimeout(() => setVideosIsTransitioning(false), 500);
  };

  const prevVideosSlide = () => {
    if (videos.length <= visibleItems || videosIsTransitioning) return;
    
    setVideosIsTransitioning(true);
    setVideosCurrentIndex((prevIndex) => {
      const prev = prevIndex - 1;
      return prev < 0 ? videos.length - 1 : prev;
    });
    
    setTimeout(() => setVideosIsTransitioning(false), 500);
  };

  const goToVideosSlide = (index: number) => {
    if (videos.length <= visibleItems) return;
    
    setVideosIsTransitioning(true);
    setVideosCurrentIndex(index);
    
    setTimeout(() => setVideosIsTransitioning(false), 500);
  };

  // Auto-advance articles carousel
  useEffect(() => {
    if (articles.length <= visibleItems) return;
    
    const interval = setInterval(() => {
      nextArticlesSlide();
    }, 5000);

    return () => clearInterval(interval);
  }, [articles.length, articlesCurrentIndex, visibleItems]);

  // Auto-advance videos carousel
  useEffect(() => {
    if (videos.length <= visibleItems) return;
    
    const interval = setInterval(() => {
      nextVideosSlide();
    }, 5000);

    return () => clearInterval(interval);
  }, [videos.length, videosCurrentIndex, visibleItems]);

  if (loading) {
    return (
      <section className={`media-coverage ${className || ''}`} aria-label="Media Coverage">
        <h3>{title}</h3>
        <div className="loading-spinner">Loading media coverage...</div>
      </section>
    );
  }

  if (error) {
    return (
      <section className={`media-coverage ${className || ''}`} aria-label="Media Coverage">
        <h3>{title}</h3>
        <div className="media-carousel" role="list">
          <div className="media-card" style={{ textAlign: 'center', padding: '20px' }}>
            <p style={{ color: '#dc3545' }}>{error}</p>
          </div>
        </div>
      </section>
    );
  }

  if (mediaItems.length === 0) {
    return (
      <section className={`media-coverage ${className || ''}`} aria-label="Media Coverage">
        <h3>{title}</h3>
        <div className="media-carousel" role="list">
          <div className="media-card" style={{ textAlign: 'center', padding: '20px' }}>
            <p>No media coverage available at this time.</p>
          </div>
        </div>
      </section>
    );
  }

  // Get visible articles for carousel
  const getVisibleArticles = (): MediaCoverageItem[] => {
    const result: MediaCoverageItem[] = [];
    for (let i = 0; i < visibleItems; i++) {
      const index = (articlesCurrentIndex + i) % articles.length;
      result.push(articles[index]);
    }
    return result;
  };

  // Get visible videos for carousel
  const getVisibleVideos = (): MediaCoverageItem[] => {
    const result: MediaCoverageItem[] = [];
    for (let i = 0; i < visibleItems; i++) {
      const index = (videosCurrentIndex + i) % videos.length;
      result.push(videos[index]);
    }
    return result;
  };

  const renderArticleItem = (item: MediaCoverageItem) => (
    <div className="media-card" key={item.id} tabIndex={0} aria-label={`${item.title} article`}>
      <div className="media-image-container">
          <LinkPreview 
          url={item.link}
           maxWidth="100%" 
           showDescription={false}
           onlyImage={true}
        />
        {item.mediaLogo ? (
          <img 
            src={item.mediaLogo} 
            alt={`${item.title} logo`}
            onError={(e) => {
              const img = e.target as HTMLImageElement;
              img.style.display = 'none';
              const initialsContainer = img.nextElementSibling as HTMLElement;
              if (initialsContainer) {
                initialsContainer.style.display = 'flex';
              }
            }}
          />
        ) : (
          <div className="media-initials">
            {item.title.split(' ').map(n => n[0]).join('')}
          </div>
        )}
      </div>
      <div className="media-name">
        <h5>{item.title}</h5>
        <p>{item.description}</p>
        <a href={item.link} target="_blank" rel="noopener noreferrer">
          Read More
        </a>
      </div>
    </div>
  );

  const renderVideoItem = (item: MediaCoverageItem) => (
    <div className="media-card" key={item.id} tabIndex={0} aria-label={`${item.title} video`}>
      <div className="youtube-video-container">
        <iframe
          width="100%"
          height="166"
          src={item.link}
          title={`${item.title} Video`}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        ></iframe>
      </div>
      <div className="media-name">
        <h5>{item.title}</h5>
        <p>{item.description}</p>
      </div>
    </div>
  );

  return (
    <section className={`media-coverage ${className || ''}`} aria-label="Media Coverage">
      <h3>{title}</h3>
      
      {/* Articles Row */}
      {articles.length > 0 && (
        <div className="media-row articles-row">
          <h4>Articles & Press</h4>
          {articles.length <= visibleItems ? (
            <div className="media-carousel" role="list">
              {articles.map(renderArticleItem)}
            </div>
          ) : (
            <div className="media-carousel-container">
              <button 
                className="carousel-arrow left-arrow" 
                onClick={prevArticlesSlide}
                aria-label="Previous article"
                disabled={articlesIsTransitioning}
              >
                ‹
              </button>
              
              <div className="media-carousel" role="list" ref={articlesCarouselRef}>
                {getVisibleArticles().map(renderArticleItem)}
              </div>
              
              <button 
                className="carousel-arrow right-arrow" 
                onClick={nextArticlesSlide}
                aria-label="Next article"
                disabled={articlesIsTransitioning}
              >
                ›
              </button>
            </div>
          )}
          
          {articles.length > visibleItems && (
            <div className="carousel-indicators" role="tablist" aria-label="Article indicators">
              {articles.map((_, index) => (
                <button
                  key={index}
                  className={`indicator-dot ${index === articlesCurrentIndex ? 'active' : ''}`}
                  onClick={() => goToArticlesSlide(index)}
                  aria-label={`Go to article ${index + 1}`}
                  aria-selected={index === articlesCurrentIndex ? 'true' : 'false'}
                  role="tab"
                  tabIndex={0}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Videos Row */}
      {videos.length > 0 && (
        <div className="media-row videos-row">
          <h4>Video Coverage</h4>
          {videos.length <= visibleItems ? (
            <div className="media-carousel" role="list">
              {videos.map(renderVideoItem)}
            </div>
          ) : (
            <div className="media-carousel-container">
              <button 
                className="carousel-arrow left-arrow" 
                onClick={prevVideosSlide}
                aria-label="Previous video"
                disabled={videosIsTransitioning}
              >
                ‹
              </button>
              
              <div className="media-carousel" role="list" ref={videosCarouselRef}>
                {getVisibleVideos().map(renderVideoItem)}
              </div>
              
              <button 
                className="carousel-arrow right-arrow" 
                onClick={nextVideosSlide}
                aria-label="Next video"
                disabled={videosIsTransitioning}
              >
                ›
              </button>
            </div>
          )}
          
          {videos.length > visibleItems && (
            <div className="carousel-indicators" role="tablist" aria-label="Video indicators">
              {videos.map((_, index) => (
                <button
                  key={index}
                  className={`indicator-dot ${index === videosCurrentIndex ? 'active' : ''}`}
                  onClick={() => goToVideosSlide(index)}
                  aria-label={`Go to video ${index + 1}`}
                  aria-selected={index === videosCurrentIndex ? 'true' : 'false'}
                  role="tab"
                  tabIndex={0}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default MediaCoverage;
